import { clearPendingScheduleDate } from '@platewell/core';
import { useConnectionStatus, useRecipeQuota } from '@platewell/query';
import { Link, useRouterState } from '@tanstack/react-router';
import type { Ref } from 'react';
import { NavIcon, type NavIconName } from './nav-icons.js';

/**
 * The primary destinations on a phone, fixed to the bottom of the viewport.
 *
 * These are the same three the native app puts in its tab bar, in the same order, so moving
 * between the two does not mean relearning where anything is. Everything else — collections,
 * tags, importing, settings — lives behind More rather than costing permanent screen space.
 *
 * Adding a recipe sits in the middle of them: the one action in a row of destinations, under
 * the thumb on every screen rather than only on the ones that list recipes.
 */
export function MobileTabbar({
  onMore,
  moreRef,
}: {
  onMore: () => void;
  moreRef: Ref<HTMLButtonElement>;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <nav className="tabbar" aria-label="Primary">
      <Tab
        to="/"
        icon="recipes"
        label="Recipes"
        active={pathname === '/'}
        onNavigate={clearPendingScheduleDate}
      />
      <Tab
        to="/favourites"
        icon="favourites"
        label="Favourites"
        active={pathname === '/favourites'}
      />
      <AddTab />
      <Tab to="/schedule" icon="schedule" label="Schedule" active={pathname === '/schedule'} />
      <button
        ref={moreRef}
        type="button"
        className="tabbar-item"
        aria-haspopup="dialog"
        onClick={onMore}
      >
        <NavIcon name="more" />
        More
      </button>
    </nav>
  );
}

/**
 * Hidden for viewers, who cannot add recipes to the household in the first place. At the plan
 * limit it stays and points at the paywall instead, for the same reason `ImportNavItem` does:
 * a control that vanished would leave someone hunting for yesterday's feature.
 *
 * The plus carries no visible label — a filled disc in the middle of a tab bar is the one
 * idiom a phone user does not need told — so the name lives on the link for a screen reader.
 */
function AddTab() {
  const { readOnly } = useConnectionStatus();
  const quota = useRecipeQuota();
  if (readOnly) return null;

  return (
    <Link className="tabbar-item tabbar-add" to={quota.atLimit ? '/subscribe' : '/recipes/new'}>
      <span className="tabbar-add-disc">
        <NavIcon name="plus" className="tabbar-add-icon" />
      </span>
      <span className="sr-only">New recipe</span>
    </Link>
  );
}

/**
 * Active state is a path comparison rather than `activeProps`, because a `<Link to="/">`
 * matches every route as a prefix — the Recipes tab would light up on Schedule too.
 */
function Tab({
  to,
  icon,
  label,
  active,
  onNavigate,
}: {
  to: string;
  icon: NavIconName;
  label: string;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      className={`tabbar-item ${active ? 'active' : ''}`}
      to={to}
      aria-current={active ? 'page' : undefined}
      onClick={() => {
        onNavigate?.();
      }}
    >
      <NavIcon name={icon} />
      {label}
    </Link>
  );
}
